import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useI18n } from '../../i18n/useI18n';
import type { LacUser } from '../../types/lac';
import { LacUserListPhoto } from '../../features/user/components/UserListPhoto';
import './LacUserRow.css';
import {
  resolveLacUserProfilePath,
  type UserProfileLocationState,
} from '../../routes/lacPaths';
import {
  formatInitials,
  resolveUserRef,
} from '../../features/user/userDisplay';

type LacUserRowProps = {
  user: LacUser;
  tenantName: string;
  knowledgeBaseCount?: number | null;
  profileLinkState?: UserProfileLocationState;
};

function formatPersonName(user: LacUser): string {
  const fullName = [user.firstName, user.lastName]
    .filter((part): part is string => Boolean(part))
    .join(' ');

  return fullName !== '' ? fullName : user.name;
}

function isCurrentAuthUser(
  listUser: LacUser,
  currentUserId: string | undefined,
): boolean {
  if (!currentUserId) {
    return false;
  }

  return listUser.id === currentUserId;
}

export function LacUserRow({
  user,
  tenantName,
  knowledgeBaseCount,
  profileLinkState,
}: LacUserRowProps) {
  const { translate } = useI18n();
  const { user: authUser } = useAuth();
  const personName = formatPersonName(user);
  const showUsername = personName !== user.name;
  const isCurrentUser = isCurrentAuthUser(user, authUser?.id);
  const profilePath = resolveLacUserProfilePath(
    tenantName,
    resolveUserRef(user),
  );
  const initials = formatInitials(personName);

  return (
    <li className="lac-users__user-item">
      <Link
        to={profilePath}
        state={profileLinkState}
        className="lac-users__user"
        aria-label={translate('lac.users.openProfile', { name: personName })}
      >
        <LacUserListPhoto
          thumbnail={user.thumbnail}
          initials={initials}
          className="lac-users__photo"
        />
        <div className="lac-users__user-person">
          <span className="lac-users__user-name">
            <span className="lac-users__user-name-text">{personName}</span>
            {isCurrentUser && (
              <span className="lac-users__you-badge">
                {translate('common.youBadge')}
              </span>
            )}
          </span>
          {showUsername && (
            <span className="lac-users__user-username">{user.name}</span>
          )}
        </div>
        <span className="lac-users__user-email">
          {user.email ?? translate('lac.users.noEmail')}
        </span>
        <span className="lac-users__user-kb">
          {knowledgeBaseCount != null
            ? translate('lac.users.knowledgeBaseCount', {
                count: String(knowledgeBaseCount),
              })
            : translate('lac.users.statusUnknown')}
        </span>
        <span className="lac-users__user-status">
          {user.enabled == null ? (
            translate('lac.users.statusUnknown')
          ) : (
            <span
              className={`lac-users__badge${
                user.enabled ? '' : ' lac-users__badge--disabled'
              }`}
            >
              {user.enabled
                ? translate('lac.users.enabled')
                : translate('lac.users.disabled')}
            </span>
          )}
        </span>
      </Link>
    </li>
  );
}
